import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { DynamicIcon } from '@/utils/iconMap';
import IconPicker from '@/components/IconPicker';
import { API_URL } from '@/config/api';

const newFeature = () => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  icon: 'Star',
  title: '',
  description: '',
  sort_order: 0,
});

const AdminHomeFeatures = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const res = await axios.get(`${API_URL}/home-features`);
      setFeatures(res.data || []);
    } catch (error) {
      console.error('Failed to fetch home features:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFeatures([...features, newFeature()]);
  };

  const handleRemove = () => {
    if (deleteTarget === null) return;
    setFeatures(features.filter((_, i) => i !== deleteTarget));
    setDeleteTarget(null);
  };

  const handleMove = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= features.length) return;
    const reordered = [...features];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setFeatures(reordered);
  };

  const updateField = (index, field, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    setFeatures(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = features.map((f, i) => ({
        id: f.id && !f.id.startsWith('temp-') ? f.id : undefined,
        icon: f.icon,
        title: f.title,
        description: f.description,
        sort_order: i,
      }));
      const res = await axios.put(`${API_URL}/home-features`, payload);
      setFeatures(res.data || []);
      toast.success('儲存成功');
    } catch (error) {
      console.error(error);
      toast.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">首頁特色管理</h1>
            <p className="text-muted-foreground mt-1">管理首頁「為何選擇」區域的 4 個特色卡片</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleAdd}>
              <Plus className="mr-2 h-4 w-4" /> 新增項目
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? '儲存中...' : '儲存全部'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">載入中...</div>
        ) : features.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <p className="text-muted-foreground mb-4">尚未有任何特色項目</p>
            <Button onClick={handleAdd}>
              <Plus className="mr-2 h-4 w-4" /> 新增第一個項目
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {features.map((feature, index) => (
              <Card key={feature.id}>
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <DynamicIcon name={feature.icon} className="h-5 w-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">項目 {index + 1}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMove(index, -1)}
                      disabled={index === 0}
                      title="上移"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMove(index, 1)}
                      disabled={index === features.length - 1}
                      title="下移"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteTarget(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <IconPicker
                        label="圖示"
                        value={feature.icon}
                        onChange={(v) => updateField(index, 'icon', v)}
                      />
                    </div>
                    <div>
                      <Label>標題</Label>
                      <Input
                        value={feature.title}
                        onChange={(e) => updateField(index, 'title', e.target.value)}
                        placeholder="例如：專業認證"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>描述</Label>
                    <Textarea
                      value={feature.description}
                      onChange={(e) => updateField(index, 'description', e.target.value)}
                      rows={2}
                      placeholder="例如：所有導師均持有合格教車執照"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {features.length > 0 && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg">
              {saving ? '儲存中...' : '儲存全部'}
            </Button>
          </div>
        )}
      </div>
      <AlertDialog open={deleteTarget !== null} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除這個項目？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷（儲存後生效）。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminHomeFeatures;
